/**
 * Which lists of a project document are lists *of things* (§5.25) — matched item
 * by item on an id when two versions are compared or merged — and how each item
 * is named for a person reading the result.
 *
 * Everything not named here is a value: an object is compared field by field,
 * and any other array — a workflow's steps, a request's headers, an egress list
 * — is compared whole. That line is deliberate. A workflow merged step by step
 * could keep an arrow into a step the other side deleted, and a header list
 * merged row by row could send a header twice; both are graphs and tables that
 * only make sense as the person who drew them left them.
 */

export interface ListShape {
  key: (item: Record<string, unknown>) => string;
  label: (item: Record<string, unknown>) => string;
}

const byId = (item: Record<string, unknown>): string => String(item.id);
const named = (item: Record<string, unknown>): string => String(item.name ?? item.id);

/** Keyed by the list's path with the list levels left out — `repos.env` is every repository's `env`. */
export const LISTS: Record<string, ListShape> = {
  repos: { key: byId, label: named },
  'repos.env': { key: (item) => String(item.key), label: (item) => String(item.key) },
  'repos.api.folders': { key: byId, label: named },
  'repos.api.requests': { key: byId, label: named },
  'repos.api.environments': { key: byId, label: named },
  'repos.api.variables': { key: byId, label: (item) => String(item.key ?? item.id) },
  'repos.api.flows': { key: byId, label: named },
  workflows: { key: byId, label: named },
  taskSources: { key: byId, label: (item) => `${String(item.kind)}:${String(item.projectKey)}` },
  'config.hostServices': { key: byId, label: (item) => String(item.label || item.envVar || item.id) },
  'config.localServices': { key: byId, label: (item) => String(item.name || item.id) }
};

export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Structural equality over JSON values — what "changed" means for a document. */
export function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === undefined || b === undefined || a === null || b === null) return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, index) => sameValue(item, b[index]));
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const key of keys) if (!sameValue(a[key], b[key])) return false;
    return true;
  }
  return false;
}

/**
 * The label each item of a list is shown under, made unique within the list: two
 * requests both called `Login` read as `Login#q_1a2b` and `Login#q_9f8e`, so a
 * diff never names one row twice.
 */
export function labels(shape: ListShape, ...lists: unknown[][]): Map<string, string> {
  const seen = new Map<string, Record<string, unknown>>();
  for (const list of lists) {
    for (const item of list) if (isPlainObject(item) && !seen.has(shape.key(item))) seen.set(shape.key(item), item);
  }
  const counts = new Map<string, number>();
  for (const item of seen.values()) counts.set(shape.label(item), (counts.get(shape.label(item)) ?? 0) + 1);
  const out = new Map<string, string>();
  for (const [key, item] of seen) {
    const label = shape.label(item);
    out.set(key, (counts.get(label) ?? 0) > 1 ? `${label}#${key}` : label);
  }
  return out;
}

export function indexed(shape: ListShape, list: unknown): Map<string, Record<string, unknown>> {
  const out = new Map<string, Record<string, unknown>>();
  if (!Array.isArray(list)) return out;
  for (const item of list) if (isPlainObject(item)) out.set(shape.key(item), item);
  return out;
}
